import React, { useEffect, useState, useRef } from "react"
import styled from "styled-components"
import { connect } from "react-redux"
import style from "./DashboardFinal.module.scss"
import {
  clearOnboardingForms,
  clearProjects,
  closeMainModal,
  openTutorialModal,
} from "../../store/actions"
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx"
import HeaderNavImproved from "../../components/Nav/HeaderNavImproved/HeaderNavImproved.tsx"
import DashboardHeader, { MobileResultsPayload } from "../../components/Dashboard/DashboardHeader/DashboardHeader.tsx"
import HomeActions from "../../components/Dashboard/HomeActions/HomeActions.tsx"
import AIAgentOrb from "../../components/Dashboard/AIAgentOrb/AIAgentOrb.tsx"
import useNavVisibility from "../../hooks/useNavVisibility.ts"
import AnimatedMeshBackground from "../../components/Background/AnimatedMeshBackground.tsx"
import { fireAnalytics } from "../../utils/firebase"
import {
  getSessionId,
  getDeviceCategory,
  getPreviousPage,
  getDashboardSource,
  checkFirstVisitToday,
  setDashboardViewTime,
  getTimeOnDashboard,
  getTabsViewedCount,
  getActionsClickedCount,
  clearDashboardTracking,
} from "../../utils/analyticsHelpers.ts"

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  padding-right: 5%;
  padding-left: 5%;
  margin: auto;
  background-color: transparent;

  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
  }

  @media (max-width: 900px) {
    padding-right: 0;
    padding-left: 0;
    min-height: 100dvh;
  }

  @media (max-width: 600px) {
    min-height: 100dvh;
  }
`

interface DashboardFinalProps {
  tutorialModal?: boolean
  openTutorialModal: () => void
  isLoaded: boolean
  clearProjects: () => void
  closeMainModal: () => void
  userData?: any
  tutorial?: any
}

const DashboardFinal: React.FC<DashboardFinalProps> = ({ 
  tutorialModal, 
  openTutorialModal,
  userData,
  tutorial,
  clearProjects,
  ...props 
}) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility()
  const [mobileResultsState, setMobileResultsState] = useState<MobileResultsPayload>({ status: 'idle' })
  const [isConversationalModeActive, setIsConversationalModeActive] = useState(false)
  const [agentStatus, setAgentStatus] = useState<'idle' | 'thinking' | 'responding' | 'error' | 'offline'>('idle')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  type ConversationalActions = {
    onCreateNewSession: () => void
    onOpenSearchModal: () => void
    onCloseConversationalMode: () => void
    hasActiveConversation?: boolean
    totalChatsCount?: number
  }
  const [conversationalActions, setConversationalActions] = useState<ConversationalActions | null>(null)
  const dashboardStartTimeRef = useRef<number | null>(null)

  useEffect(() => {
    clearOnboardingForms()
    clearProjects()
    
    setDashboardViewTime()
    dashboardStartTimeRef.current = Date.now()
    
    fireAnalytics('dashboard_view', {
      user_id: userData?.id || '',
      session_id: getSessionId(),
      source: getDashboardSource(),
      previous_page: getPreviousPage(),
      is_first_visit_today: checkFirstVisitToday(),
      user_type: userData?.type || '',
      device_category: getDeviceCategory(),
      category: 'dashboard',
      action: 'view'
    })
    
    return () => {
      const timeSpent = dashboardStartTimeRef.current 
        ? Math.floor((Date.now() - dashboardStartTimeRef.current) / 1000)
        : getTimeOnDashboard()
      
      if (timeSpent > 0) {
        fireAnalytics('dashboard_engagement', {
          user_id: userData?.id || '',
          session_id: getSessionId(),
          engagement_time_msec: timeSpent * 1000,
          tabs_viewed: getTabsViewedCount(),
          actions_clicked: getActionsClickedCount(),
          device_category: getDeviceCategory(),
          category: 'dashboard',
          action: 'engagement'
        })
      }
      
      clearDashboardTracking()
    }
  }, [userData, clearProjects])

  useEffect(() => {
    if (!isConversationalModeActive) {
      setSidebarCollapsed(false)
    }
  }, [isConversationalModeActive])

  useEffect(() => {
    try {
      if (!tutorial) {
        return
      }

      const tutorialState = tutorial
      const lastShownDate = tutorialState?.lastShownDate
      const today = new Date().toDateString()
      
      const isCompleted = tutorialState?.completed ?? false
      const isSkipped = tutorialState?.skipped ?? false
      
      if (!isCompleted && !isSkipped) {
        if (!lastShownDate || lastShownDate !== today) {
          openTutorialModal()
        }
      }
    } catch (error) {
      console.error('Error checking tutorial state:', error)
    }
  }, [tutorial, openTutorialModal])

  return (
      <Container>
      <AnimatedMeshBackground />
        {!ocultarEnMobile && (
          <HeaderNavImproved
            isConversationalModeActive={isConversationalModeActive}
            onCreateNewSession={conversationalActions?.onCreateNewSession}
            onOpenSearchModal={conversationalActions?.onOpenSearchModal}
            onCloseConversationalMode={conversationalActions?.onCloseConversationalMode}
            hasActiveConversation={conversationalActions?.hasActiveConversation}
            totalChatsCount={conversationalActions?.totalChatsCount}
            isSidebarCollapsed={sidebarCollapsed}
          />
        )}
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
        <div className={style.mainContainer}>
          {!isConversationalModeActive && <AIAgentOrb status={agentStatus} />}
          <DashboardHeader 
            userName={
              userData?.type === "COMPANY" 
                ? userData?.razon_social 
                : userData?.name || "Usuario"
            }
            userId={userData?.id}
            userData={userData}
            onMobileResultsChange={setMobileResultsState}
            onConversationalModeChange={setIsConversationalModeActive}
            onAgentStatusChange={(isProcessing) => {
              setAgentStatus(isProcessing ? 'thinking' : 'idle')
            }}
            onConversationalActionsReady={(actions) => {
              setConversationalActions(actions)
            }}
            onSidebarStateChange={setSidebarCollapsed}
            sidebarCollapsed={sidebarCollapsed}
          />
          
          {ocultarEnMobile && !isConversationalModeActive && (
            <div className={style.mobileActionsPlaceholder}>
              <span>Contanos qué querés hacer y te ayudamos con la IA de Edify</span>
            </div>
          )}
          
          {!ocultarEnMobile && !isConversationalModeActive && (
            <div className={style.actionsSection}>
              <HomeActions />
            </div>
          )}
        </div>
      </Container>
    
  )
}

const mapStateToProps = (state: any) => {
  return {
    isLoaded: state.general?.isLoaded,
    userData: state.general?.userData,
    tutorial: state.dashboard?.tutorial,
  }
}

export default connect(mapStateToProps, {
  clearProjects,
  closeMainModal,
  openTutorialModal,
})(DashboardFinal)

